import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  fetchProgramCellBgPalette,
  mutateProgramCellBgPalette,
  readProgramCellBgPaletteCache,
} from '../services/programCellBgPaletteService';
import { normalizeProgramCellBgValue } from '../utils/programCellBgStyle';

type Listener = (colors: string[]) => void;
const listeners = new Set<Listener>();
function broadcast(colors: string[]) {
  listeners.forEach((fn) => fn(colors));
}

/** 프로그램 테이블 셀 배경색 등록 팔레트 (사용자별, DB 저장) */
export function useProgramCellBgPalette(enabled = true) {
  const [userId, setUserId] = useState<string | null>(null);
  const [colors, setColors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      const uid = data.session?.user?.id ?? null;
      setUserId(uid);
      if (!uid) return;
      const cached = readProgramCellBgPaletteCache(uid);
      if (cached) setColors(cached.colors);
      fetchProgramCellBgPalette(uid)
        .then((snap) => {
          if (!cancelled) setColors(snap.colors);
        })
        .catch(() => {
          // 캐시 표시 유지
        });
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  useEffect(() => {
    const fn: Listener = (next) => setColors(next);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);

  const mutate = useCallback(
    async (fn: (prev: string[]) => string[]) => {
      if (!userId) return;
      setBusy(true);
      try {
        const snap = await mutateProgramCellBgPalette(userId, fn);
        broadcast(snap.colors);
      } finally {
        setBusy(false);
      }
    },
    [userId]
  );

  const addColor = useCallback(
    (hex: string) => {
      const n = normalizeProgramCellBgValue(hex);
      if (!n) return Promise.resolve();
      return mutate((prev) => (prev.includes(n) ? prev : [...prev, n]));
    },
    [mutate]
  );

  const replaceColor = useCallback(
    (fromHex: string, toHex: string) => {
      const from = normalizeProgramCellBgValue(fromHex);
      const to = normalizeProgramCellBgValue(toHex);
      if (!from || !to) return Promise.resolve();
      return mutate((prev) => {
        if (from === to) return prev;
        const idx = prev.indexOf(from);
        const without = prev.filter((c) => c !== to);
        if (idx < 0) return [...without, to];
        return without.map((c) => (c === from ? to : c));
      });
    },
    [mutate]
  );

  const removeColor = useCallback(
    (hex: string) => {
      const n = normalizeProgramCellBgValue(hex);
      if (!n) return Promise.resolve();
      return mutate((prev) => prev.filter((c) => c !== n));
    },
    [mutate]
  );

  return { colors, busy, addColor, replaceColor, removeColor };
}
